/** «Чаты»: общий мессенджер клиентов (Telegram, MAX, Авито, ВКонтакте, WhatsApp) — `/api/chats`. */
import { ApiFailure, apiFetch, query } from "@/lib/api";
import type { StageKind } from "@/lib/dealStage";

export type Channel = "telegram" | "max" | "avito" | "vk" | "whatsapp" | "instagram" | "livechat";
export type FileKind = "photo" | "video" | "animation" | "video_note" | "voice" | "audio" | "sticker" | "document" | "product";
export type ChatFile = {
  kind: FileKind;
  name: string;
  mime: string;
  size: number | null;
  width: number | null;
  height: number | null;
  duration: number | null;
  /** Файл у нас; `null` — площадка файлом не отдаёт (видео ВК), тогда есть `link`. */
  url: string | null;
  thumb_url: string | null;
  link: string | null;
  /** Товар площадки (`kind = "product"`): цена строкой и артикул. */
  price?: string | null;
  sku?: string | null;
  /** Голосовое текстом; `null` — ещё не расшифровано или не вышло. */
  transcript?: string | null;
  /** Файл Telegram больше 20 МБ: бот его не скачает, открыть можно только в Telegram. */
  too_big: boolean;
};
export type ChatLast = { out: boolean; date: string | null; text: string; kind: FileKind | null };
/** Сделка диалога из своей копии Bitrix: стадия, её вид, дата записи («wait») или следующей
 * обработки, даты проката (`ГГГГ-ММ-ДД`) для «Забронировано»/«Ожидаем возврат». */
export type ChatDealInfo = {
  id: number;
  stage: string;
  stage_code: string;
  kind: StageKind;
  date: string | null;
  rent_start: string | null;
  rent_end: string | null;
  /** Номера сделки (карточка чата, панель): сверка с номером диалога — `lib/dealPhones`. */
  phones?: string[];
};
/** Лид диалога, когда сделки нет: статус, в работе ли, дата следующей обработки. */
export type ChatLeadInfo = { id: number; status: string; status_code: string; active: boolean; date: string | null };
export type Chat = {
  id: number;
  channel: Channel;
  external_id: string;
  name: string;
  username: string;
  avatar: string;
  /** Авито: объявление (`item`, `item_url`, `city`). Instagram: город аккаунта (`title`). */
  context: {
    item?: string | null;
    item_url?: string | null;
    city?: string | null;
    /** Онлайн-чат сайта: страница, откуда написали; `open` — диалог линии Bitrix ещё открыт. */
    page?: string | null;
    open?: boolean;
    title?: string | null;
    /** Переписка личного/рабочего аккаунта сотрудника из расширения Chrome — только чтение. */
    web?: {
      account_id: number;
      /** Как номер назван в CRM (`sMsg_whats_phone`); пусто — не назван. */
      account_name: string;
      account_phone?: string | null;
      platform?: string;
      peer_id: string;
      /** Чат завели по номеру клиента (`POST /api/chats/start`), диалога во вкладке ещё нет. */
      by_phone?: boolean;
    } | null;
  } | null;
  /** Групповой чат сотрудников (группа бота или из браузера): в кабинете только читаем. */
  group?: boolean;
  /** Группа у меня «без уведомлений»: её непрочитанные не идут в число на «Группах», как в Telegram. */
  muted?: boolean;
  /**
   * Где шла переписка — наш аккаунт: `web` — аккаунт сотрудника из браузера (имя номера в CRM и
   * номер), иначе общий аккаунт канала: номер Wazzup у WhatsApp, город у Instagram; у ботов,
   * Авито и ВК имя и номер пустые.
   */
  account: { web: boolean; name: string; phone: string; online?: AccountOnline };
  unread: number;
  /** Отметка «ответили» руками (ответ ушёл не отсюда), MSK; новое сообщение клиента снимает. */
  answered: string | null;
  /**
   * Клиент ждёт ответа: последнее сообщение, не считая автоматических (приветствие, ответ
   * бота, шаблон рассылки, `/start`), — клиента. Приветствие после его сообщения ответом не считается.
   */
  waits: boolean;
  /** Активный номер клиента и привязка к CRM по своей базе (`chat_deal_sync`). */
  phone: string | null;
  deal: ChatDealInfo | null;
  lead: ChatLeadInfo | null;
  last: ChatLast;
  /** Теги чата — общие метки сотрудников, по алфавиту; по ним фильтруется список. */
  tags: string[];
  /** Другие чаты того же клиента (та же сделка или тот же номер), по id. */
  related: ChatBrief[];
  /** WhatsApp: окно 24 ч от последнего сообщения клиента — вне окна текст уходит платным шаблоном. */
  whatsapp?: { open: boolean; last_in: string | null; until: string | null };
  /** ВКонтакте: клиент закрыл сообщения сообществу (`can_write` API ВК, 901/902) — почему не уйдёт. */
  vk_closed?: string | null;
  /** Черновик ответа ИИ ждёт решения (приходит в списке тем, у кого право `chat_suggest`). */
  suggest?: boolean;
  /** Активных задач чата — пересылок в салон, которые сотрудник ещё не отметил завершёнными. */
  tasks?: number;
  /** Чат личного аккаунта (`context.web`): можно ли ответить из кабинета — ответ набирается в диалоге
   *  вкладки служебного браузера; `reason` — почему нельзя. */
  web_send?: { ok: boolean; reason: string; port: number | null };
  /** Группа сотрудников: можно ли ответить отсюда — в группе наш бот (Telegram, MAX). */
  group_send?: boolean;
  /** Можно ли ответить на конкретное сообщение (реплай): Telegram, MAX, ВК, WhatsApp; не Авито,
   *  Instagram и переписка из браузера. */
  reply?: boolean;
};
/**
 * Кто из сотрудников сейчас на нашем канале. `kind` — по чему судим: `tab` — номер из браузера,
 * отметка вкладки расширения за 15 минут; `reply` — каналы, где отвечают из «Чатов» (Wazzup, боты,
 * Авито, ВК, Instagram): ответ сотрудника за 20 минут в ЛЮБОЙ из них — отвечают на них с одного
 * экрана, поэтому отметка у всех таких чатов одна. `users` — кто (свежие первыми), `last` — кто
 * был последним (вкладка — за 30 дней, ответ — за 7).
 */
export type AccountOnline = {
  kind: "tab" | "reply";
  active: boolean;
  users: AccountOnlineUser[];
  last: AccountOnlineUser | null;
};
export type AccountOnlineUser = {
  id: number;
  name: string;
  seen: string;
  /** Секунд с `seen`; в ответах «Чатов» его нет — считает фронт (`AccountOnline.ageOf`). */
  age?: number;
  /** `reply`: ответов за 20 минут, канал последнего ответа и ответы по каналам. */
  replies?: number;
  channel?: Channel;
  channels?: Partial<Record<Channel, number>>;
};
/** Ответ сотрудника из «Чатов» — где именно он ответил (окно «Кто отвечает из «Чатов»»). */
export type StaffReply = { chat_id: number; chat: string; channel: Channel; date: string; text: string; file: boolean };
export const loadStaffReplies = (users: number[]) =>
  apiFetch<{ users: Record<string, StaffReply[]> }>(
    `/api/chats/replies?${users.map((u) => `user=${u}`).join("&")}`,
  );
/** Чат без привязки к CRM — так приходят связанные чаты клиента. */
export type ChatBrief = Pick<
  Chat,
  "id" | "channel" | "external_id" | "name" | "username" | "avatar" | "context" | "account" | "unread" | "answered" | "waits" | "last"
>;
export type ChatReply = {
  id: number | null;
  author: string;
  out: boolean | null;
  user_id: number | null;
  text: string;
  file_kind: string | null;
};
export type ChatMessage = {
  id: number;
  /** Чат сообщения: в общей ленте клиента сообщения идут из нескольких его чатов. */
  chat_id: number;
  out: boolean;
  date: string;
  text: string;
  author: string;
  /** Сотрудник CRM, написавший сообщение (из «Чатов», в группе — и из самого мессенджера); `null` — не знаем. */
  user_id?: number | null;
  /** Группа: сообщение самого бота (сводки, пересылки в салон) — в ленте справа, как своё. */
  bot?: boolean;
  /** Автоматическое (приветствие, ответ бота, шаблон рассылки, `/start` клиента) — не ответ сотрудника. */
  auto: boolean;
  file: ChatFile | null;
  /** WhatsApp и ответы через служебный браузер, наши сообщения: `sent` · `delivered` · `read` ·
   *  `error` (тогда есть `error`). Ответ через браузер, который не ушёл или не подтверждён, приходит
   *  строкой с отрицательным `id`: в базе сообщений его нет. `sending` — ответ ещё уходит: либо
   *  строка сервера (ответ набирается в служебном браузере, шаг — в `step`), либо своя, пока сервер
   *  не ответил. */
  status?: string | null;
  error?: string | null;
  /** Шаг набора в служебном браузере (`status: "sending"`): tunnel · tab · dialog · field · type ·
   *  click · wait · tick. */
  step?: string | null;
  /** Кнопки шаблона рассылки с настоящими адресами; у «Время записи» — что за ссылкой сейчас. */
  buttons?: ChatButton[];
  /** Сообщение исправили на площадке (ВК): что было до каждой правки и когда исправили; `text` — что стало. */
  edits?: { text: string; date: string }[];
  /** Сообщение рассылки, нажатие её кнопки клиентом или наш автоответ на нажатие. */
  mailing?: ChatMailingMark;
  /** Ответ на сообщение (реплай): исходное из этого же чата; `id: null` — его нет в нашей копии,
   *  тогда `text` — цитата, которую прислала площадка. */
  reply?: ChatReply;
};
/**
 * Отметка рассылки на сообщении. У самой рассылки — `buttons`: кнопки под ней, `at` — когда клиент
 * нажал (null — не нажал), и `then` — нажатия дальше по цепочке (переспрос отмены примерки). У
 * нажатия клиента — `press`: подпись кнопки. У автоответа — только название.
 */
export type ChatMailingPress = { step: string; label: string; at: string | null };
export type ChatMailingMark = {
  kind: string;
  title: string;
  buttons?: ChatMailingPress[];
  then?: ChatMailingPress[];
  press?: string;
};
export type ChatButton = {
  label: string;
  url: string;
  kind: "map" | "zapis" | "max" | "telegram";
  /** `at` — на когда записана сделка (местное время салона), null — не в «Ждем клиента»;
   * по ссылке клиент: `opens` — приходил по ней (важен факт, не число), `books` — записался,
   * `moves` — перенёс. */
  zapis?: { deal_id: number; at: string | null; opens: number; books: number; moves: number };
};
/**
 * Номер телефона чата: `chat` — сам номер WhatsApp, `manual` — привязал сотрудник (кто и когда),
 * `text` — клиент написал в сообщении (когда). Номер чата и привязанные сверху, затем из текста
 * по последнему упоминанию; первый — активный, по нему панель ищет сделку.
 */
/**
 * Подсказка «откуда номер» по диапазонам Резерва (`phone/region.py`): регион, где номер выдан, —
 * клиент может жить в другом. `salon` — салон CRM региона, если регион его ещё не называет.
 */
export type PhoneRegion = { text: string; location_id: number | null; salon: string | null };
export const regionText = (r: PhoneRegion) => (r.salon ? `${r.text} → салон ${r.salon}` : r.text);
export const regionTitle = (r: PhoneRegion) =>
  `Номер выдан: ${regionText(r)} — по диапазонам номеров Резерва; клиент может жить в другом городе`;
export type ChatPhone = {
  phone: string;
  source: "chat" | "manual" | "text";
  date: string;
  user: string;
  region: PhoneRegion | null;
};
/** Сделки и лиды чата: по ссылке брони и по сессиям открытой линии Bitrix; номера — `phones`;
 * `phone_checked` — активный номер портал уже видел (иначе панель один раз спросит живой Bitrix);
 * `deals_active` — все сделки клиента в работе, новые сверху: переключатель панели. */
export type ChatActiveDeal = { id: number; stage: string; title: string; rent_start: string | null; rent_end: string | null };
export type ChatThread = {
  chat: Chat & {
    deals: number[];
    leads: number[];
    phones: ChatPhone[];
    phone_checked: boolean;
    deals_active?: ChatActiveDeal[];
  };
  messages: ChatMessage[];
  /** Звонки клиента — той же лентой: номера чата, окно ленты (`chat/calls.py`). */
  calls: ChatCall[];
  /** Комментарии сотрудников из CRM — той же лентой; старый сервер поля не отдаёт. */
  comments?: ChatComment[];
  /** Приходы клиента в салон с сердечками — карточкой в ленте (`chat/visits.py`). */
  visits?: ChatVisit[];
  /** Уведомления, ушедшие автоматически по сделке чата в группы салона (невозврат). */
  notices?: ChatNotice[];
};
/**
 * Звонок клиента в ленте чата. `out` — исходящий, `answered` — соединились, `talk` — был ли живой
 * разговор по разметке модели (`talk` · `none` · `unclear`), `record` — есть запись: её отдаёт
 * `GET /api/calls/{id}/record`, `transcript` — расшифровка целиком.
 */
export type ChatCall = {
  id: number;
  date: string;
  out: boolean;
  phone: string;
  answered: boolean;
  duration: number;
  talk: string | null;
  user: string;
  user_id: number | null;
  record: boolean;
  transcript: string;
  lead_id: number | null;
};
/**
 * Комментарий сотрудника из CRM в ленте чата — клиент его не видел. `kind`: `deal` — к сделке,
 * `visit` — к приходу клиента в салон (карточка `sCustomer`, `visit` — дата и салон), `item` — к товару
 * сделки (`item` — «название · серия», артикул не выводим). `auto` — запись робота или служебной учётки.
 * Дата — московское время без пояса, как у звонков. Спека `docs/specs/2026-10-22-chat-crm-comments-design.md`.
 */
export type ChatComment = {
  id: number;
  date: string;
  text: string;
  user: string | null;
  kind: "deal" | "visit" | "item";
  deal_id: number | null;
  visit: { id: number; date: string | null; location: string | null } | null;
  item: string | null;
  task: string | null;
  /** Статус задачи CRM (`sSerial_comment_x_task`); `done_id` — комментарий, которым её закрыли. */
  task_status: { id: number; name: string; done: boolean; date: string | null; done_id: number | null } | null;
  auto: boolean;
  /** Фото, видео и голосовые, приложенные к заметке в CRM (файлы отдаёт CRM). */
  files: { kind: "photo" | "video" | "audio"; url: string; preview: string | null }[];
};
/**
 * Уведомление, которое ушло автоматически по сделке чата в группу салона (`chat/notices.py`): куда,
 * стадия цепочки, последний ответ; `group_chat_id` + `message_id` — само сообщение в группе «Чатов».
 */
export type ChatNotice = {
  id: number;
  kind: "nonreturn" | "notissued" | "notreturned"; // невозврат в «Head» · не выдали / не вернули сегодня в «Общее»
  title: string;
  date: string;
  deal_id: number;
  group: string;
  group_chat_id: number | null;
  message_id: number | null;
  state: "sent" | "answered" | "returned" | "failed";
  state_label: string;
  asks: number;
  answers: number;
  returned: string | null;
  test: boolean;
  reply: { name: string; text: string; at: string } | null;
};
/** Сердечко: товар, который стилист отметил клиенту на примерке (`sCustomer_product`). */
export type ChatHeart = {
  id: number;
  serial: number | null;
  item: string | null;
  photo: string | null;
  status: string | null;
  like: boolean;
  dislike: boolean;
  price: number | null;
  comment: string | null;
  date: string;
};
/** Приход клиента в салон — карточка клиента CRM (`sCustomer`): тип, итог, стилист, сердечки. */
export type ChatVisit = {
  id: number;
  deal_id: number | null;
  date: string;
  finish: string | null;
  type: string | null;
  status: string | null;
  finished: boolean;
  location: string | null;
  user: string | null;
  hearts: ChatHeart[];
};
/** `counts` — чатов с непрочитанными по каналам; `unanswered` — без ответа (последнее сообщение клиента, отметки нет). */
/**
 * Выборка чатов по ссылке из «Рассылки → По шаблонам» (`/chats?mailing=&from=&to=&step=`):
 * получатели шаблона `mailing` (`all` — всех шаблонов) за дни отправки, дошедшие до шага `step`.
 */
export type MailingPick = { mailing: string; from: string; to: string; step: string };
/**
 * Выборка чатов по ссылке из «Записей» (`/chats?zapis=&zuser=&from=&to=`): чаты, где сотрудник
 * `zuser` (пусто — все) записал клиента способом `zapis` — `chat` перепиской, `link` ссылкой — за дни записи.
 */
export type ZapisPick = { zapis: string; zuser: string; from: string; to: string };
/**
 * Выборка по ссылке со страницы вопроса базы знаний (`/chats?kbq=&days=`, со `view=calls` — звонки):
 * где за `days` дней клиенты задавали вопрос `kbq`.
 */
export type KbqPick = { kbq: string; days: string };
/** Плашка отбора по вопросу: чатов и звонков за период; `messages` — чат → сообщение-вопрос. */
export type KbqInfo = { id: number; text: string; days: number; chats: number; calls: number; messages: Record<string, number> };

export type ChatList = {
  chats: Chat[];
  counts: Partial<Record<Channel, number>>;
  unanswered: Partial<Record<Channel, number>>;
  /** Из `unanswered` — чаты наших номеров, куда ответ из кабинета уходит через мост. */
  unanswered_bridge: Partial<Record<Channel, number>>;
  /** Из `unanswered` — чаты, где ответить можно только в браузере сотрудника. */
  unanswered_web: Partial<Record<Channel, number>>;
  /** Личка без ответа здесь и через мост, общая на всех (то же, что `ChatBadges`). */
  unanswered_person: number;
  /** Групп, непрочитанных мной (написали другие после того, как я открыл группу); у строки группы
   * `unread` — мои непрочитанные в ней. */
  unread_groups: number;
  /** По типу групп: сколько видно и сколько непрочитано (без «без уведомлений») — числа на кнопках отбора. */
  group_types_count?: Record<GroupType, { chats: number; unread: number }>;
  /** Чатов, где черновик ответа ИИ ждёт решения; поля нет — у сотрудника нет права `chat_suggest`. */
  suggest_count?: number;
  /** Чатов с активными задачами (пересылками в салон) и с завершёнными за 30 дней. */
  task_count?: number;
  task_done_count?: number;
  /**
   * Есть при выборке по рассылке: название шаблона, шаг, период, сколько отправок в выборке
   * (`sends` — число из таблицы рассылок) и сколько чатов (`count`): номеру могло уйти несколько.
   */
  mailing?: { kind: string; title: string; step: string; from: string; to: string; sends: number; count: number };
  /** Есть при выборке по записям: способ, сотрудник, сколько человек записано (`people` — число из «Записей») и чатов (`count`). */
  zapis?: { method: string; label: string; user: string; user_name: string; from: string; to: string; people: number; count: number };
  /** Есть при выборке по вопросу базы знаний. */
  kbq?: KbqInfo;
};

/**
 * Где можно ответить клиенту: `here` — из кабинета, официальным входом канала или через мост
 * (наш номер в служебном браузере); `web` — только в браузере сотрудника.
 */
export type ReplyPlace = "" | "here" | "web";
/** Числа на вкладках «Личка» и «Группы» — опрос отдельно от списка, видны и с «Обзвона». */
export type ChatBadges = { unanswered_person: number; unread_groups: number };
export const loadChatBadges = (groupTypes: GroupType[]) =>
  apiFetch<ChatBadges>(`/api/chats/badges${groupTypes.length ? `?${groupTypes.map((g) => `gtype=${g}`).join("&")}` : ""}`);
/**
 * Фильтры панели — сужают и список, и счётчики: `tags` — стоит любой из тегов, `cities` — город
 * чата (салон сделки, иначе лида, иначе площадки), `accounts` — наш аккаунт, на который писали
 * (ключи из `loadChatFilters`), `sources` — источник: `<канал>` — официальный вход (бот, Авито,
 * группа ВК, номер Wazzup, аккаунты Instagram), `web:<канал>` — аккаунты сотрудников этого канала
 * в браузере, `stages` — стадия, в которой сделка чата сейчас (код стадии Bitrix, `none` — сделки
 * нет), `mailings` — рассылка, которая уходила в чат: `<вид>`, `<вид>:<шаг>` — клиент нажал её
 * кнопку, `<вид>:none` — не нажал, `reports` — отчёт по сделке чата (`delivery_today` /
 * `delivery_tomorrow` — доставка или забор сегодня / завтра, `nonreturn` — не вернули, `notpicked` — не забрали, `partial` — выдан частично, `lk_refit` / `lk_delivery` —
 * заявка клиента из личного кабинета на повторку / доставку). В адресе — повтором `tag=`,
 * `city=`, `account=`, `source=`, `stage=`, `sent=`, `report=`.
 */
/** Отбор «Задачи»: чаты с активными пересылками в салон или с завершёнными. */
export type TaskPick = "" | "active" | "done";
export type ChatPanelFilter = {
  tags: string[];
  cities: string[];
  accounts: string[];
  sources: string[];
  stages: string[];
  mailings: string[];
  reports: string[];
};
/** Вид чата списка: личка клиента (по умолчанию), групповой чат сотрудников или всё вместе. */
export type ChatKind = "person" | "group" | "all";
/** Тип группы для отбора на «Группах»: «Общее <город>», «Head …» и все прочие. */
export type GroupType = "general" | "head" | "other";
export const GROUP_TYPES: { id: GroupType; label: string; title: string }[] = [
  { id: "general", label: "Общие", title: "Группы салонов «Общее <город>»" },
  { id: "head", label: "Head", title: "Группы руководителей «Head …»" },
  { id: "other", label: "Разные", title: "Все остальные группы: «Новости КЦ», «Прием Платьев», AI-группы…" },
];

/**
 * Строка вкладки «Обзвон»: лид `NEW` или сделка обзвона на вошедшем сотруднике. `chat` — последний
 * по времени чат клиента (остальные — его связанные), `calls_today` — наших исходящих на номер
 * сегодня. Спека `docs/specs/2026-10-20-chat-dial-design.md`.
 */
export type DialMissed = {
  /** Последний пропуск эпизода и первый (с него считаем попытки), ждал секунд, линия словами. */
  at: string;
  from: string;
  count: number;
  wait: number;
  line: string;
  /** Наших исходящих после пропуска; с 3 номер уходит из очереди («пытались»). */
  attempts: number;
  /** Когда был последний из них; без попыток — `null`. */
  last_try: string | null;
};
export type DialItem = {
  key: string;
  /**
   * `missed` — пропущенный входящий без перезвона, номера нет среди моих лидов и сделок: `id` = номер;
   * `call` — строка вкладки «Звонки»: `id` = звонок, сделка и лид — номера.
   */
  kind: "lead" | "deal" | "missed" | "call";
  id: number;
  name: string;
  title: string;
  phone: string | null;
  region: PhoneRegion | null;
  created: string | null;
  deal: ChatDealInfo | null;
  lead: ChatLeadInfo | null;
  chat: { id: number; channel: Channel } | null;
  chats: { id: number; channel: Channel }[];
  calls_today: number;
  /** Последний наш исходящий на номер за месяц — по нему порядок списка и подпись «звонили». */
  last_out: string | null;
  last_call: string | null;
  /** На ком строка — только в режиме «Все» (проверка владельцем). */
  owner: string | null;
  /** Ждёт перезвона после пропущенного входящего — красным, сверху списка. */
  missed: DialMissed | null;
  /** Строка из поиска по номеру без чата (`components/chat/Dial.foundItems`): звонки не считали. */
  found?: true;
  /** Сам звонок — у строк вкладки «Звонки». */
  call?: DialCall;
};
/**
 * Звонок вкладки «Звонки»: `talk` — разговор, `none` — соединились, разговора не было (разметка
 * расшифровки), `missed` — входящий не взяли, `noanswer` — наш исходящий не дозвонился.
 */
export type DialCall = {
  id: number;
  direction: "in" | "out";
  at: string;
  duration: number;
  result: "talk" | "none" | "missed" | "noanswer";
  record: boolean;
};
/**
 * Мои последние звонки (`all` — всех сотрудников, `owner` — кто говорил); сервер перед ответом докачивает свежие из Bitrix.
 * `kbq` — все звонки, где задали вопрос базы знаний, без «Мои / Все» и без докачки.
 */
export const loadMyCalls = (scope: DialScope = "mine", kbq: KbqPick | null = null) =>
  apiFetch<{ items: DialItem[]; errors: string[]; scope: DialScope; kbq?: KbqInfo }>(
    `/api/chats/calls${kbq ? `?kbq=${kbq.kbq}&days=${kbq.days}` : scope === "all" ? "?scope=all" : ""}`,
  );
/** `mine` — на мне; `all` — все лиды `NEW` и сделки обзвона (только владельцу, `all_allowed`). */
export type DialScope = "mine" | "all";
export type DialList = { items: DialItem[]; errors: string[]; scope: DialScope; all_allowed: boolean };
export const loadDial = (scope: DialScope = "mine") =>
  apiFetch<DialList>(`/api/chats/dial${scope === "all" ? "?scope=all" : ""}`);

/** Что под номером из текста сообщения или поиска: сделка, лид, договор и клиент CRM — с чатом клиента
 * или телефоном; договор и клиент проверяются только явным видом (поиск «Чатов»). */
export type NumberKind = "deal" | "lead" | "serial" | "contract" | "customer";
export type NumberItem = {
  kind: NumberKind;
  id: number;
  title: string;
  sub: string;
  chat_id: number | null;
  phone: string | null;
  /** У договора и клиента CRM — их сделка. */
  deal_id?: number | null;
};
export type NumberFound = { n: number; items: NumberItem[]; errors: string[] };
export const loadChatNumber = (n: string, kinds: NumberKind[]) =>
  apiFetch<NumberFound>(`/api/chats/number?n=${n}${kinds.map((k) => `&kind=${k}`).join("")}`);

export const loadChats = (
  channel: Channel | "",
  q: string,
  unanswered: boolean,
  panel: ChatPanelFilter,
  place: ReplyPlace = "",
  pick: MailingPick | ZapisPick | KbqPick | null = null,
  /** Только чаты, где черновик ответа ИИ ждёт решения. */
  drafts = false,
  tasks: TaskPick = "",
  /** Вид чата: личка клиента или групповой чат сотрудников. */
  kind: ChatKind = "person",
  /** Только группы этих типов (пусто — все); сужает и число на «Группах». */
  groupTypes: GroupType[] = [],
) => {
  const search = new URLSearchParams(query({ channel, q, unanswered, place, drafts, tasks, kind, ...pick }));
  groupTypes.forEach((g) => search.append("gtype", g));
  panel.tags.forEach((t) => search.append("tag", t));
  panel.cities.forEach((c) => search.append("city", c));
  panel.accounts.forEach((a) => search.append("account", a));
  panel.sources.forEach((s) => search.append("source", s));
  panel.stages.forEach((s) => search.append("stage", s));
  panel.mailings.forEach((m) => search.append("sent", m));
  panel.reports.forEach((r) => search.append("report", r));
  return apiFetch<ChatList>(`/api/chats${search.size ? `?${search}` : ""}`);
};
/** Варианты панели фильтров с числом чатов: города, источники и наши аккаунты, на которые писали клиенты. */
export type ChatCity = { id: number; name: string; short: string; chats: number };
/**
 * Наш аккаунт: общий аккаунт канала (номер Wazzup, бот, Авито, группа ВК), аккаунт Instagram
 * города (`name`) или аккаунт сотрудника в браузере (`web`: имя номера в CRM и сам номер).
 */
export type ChatAccount = { key: string; channel: Channel; web: boolean; name: string; phone: string; chats: number };
/**
 * Источник: официальный вход канала (`key` = канал) или его аккаунты сотрудников в браузере
 * (`web`, `key` = `web:<канал>`) — Wazzup и WhatsApp в браузерах врозь. Браузер — только где есть чаты.
 */
export type ChatSource = { key: string; channel: Channel; web: boolean; chats: number };
/**
 * Стадия, в которой сейчас сделки чатов: `code` — код Bitrix (`none` — «Без сделки»), `group` —
 * воронка («Прокат», «Сдача»; отказы воронки — «Прокат — отказ»), у «Без сделки» пусто.
 */
export type ChatStage = { code: string; name: string; group: string; chats: number };
/**
 * Рассылка, которая уходила в чаты: `key` — вид (`fitting`) или вид с шагом (`fitting:good` —
 * клиент нажал кнопку, `fitting:none` — не нажал ни одной); `title` — название рассылки, `name` —
 * оно же или подпись кнопки, `group` — пусто у самой рассылки, у шагов — «… — что нажал клиент».
 */
export type ChatMailing = { key: string; name: string; title: string; group: string; chats: number };
/**
 * Отчёт о выдаче сделки чата: «Не вернули» (`nonreturn`: «Ожидаем возврат», прокат кончился вчера или раньше, вещь на руках),
 * «Не забрали» (`notpicked`: «Забронировано», прокат начался, выдали не всё), «Выдан частично»
 * (`partial`: «Забронировано», часть выдали); заявки клиента со страницы брони за 30 дней —
 * «ЛК: повторная примерка» (`lk_refit`) и «ЛК: доставка» (`lk_delivery`); «Доставка сегодня» /
 * «Доставка завтра» (`delivery_today` / `delivery_tomorrow`: везём к началу проката или забираем в
 * конце в этот день, как числа дня «Доставок»). `group` — раздел («Доставка», «Выдача», «Личный кабинет»), `deals` — сделок всего, `chats` — с чатом.
 */
export type ChatReport = { key: string; name: string; group?: string; chats: number; deals: number };
/** `reports`, `sources`, `stages` и `mailings` сервер до выкатки не отдаёт — тогда эти разделы пусты. */
export type ChatFilterOptions = {
  reports?: ChatReport[];
  cities: ChatCity[];
  sources?: ChatSource[];
  stages?: ChatStage[];
  mailings?: ChatMailing[];
  accounts: ChatAccount[];
};
export const loadChatFilters = () => apiFetch<ChatFilterOptions>("/api/chats/filters");
/** Все теги чатов с числом чатов — варианты фильтра и подсказки в поле. */
export type ChatTagInfo = { name: string; chats: number };
export const loadChatTags = () => apiFetch<{ tags: ChatTagInfo[] }>("/api/chats/tags");
export const addChatTag = (chatId: number, name: string) =>
  apiFetch<{ tags: string[] }>(`/api/chats/${chatId}/tags`, { method: "POST", body: JSON.stringify({ name }) });
export const removeChatTag = (chatId: number, name: string) =>
  apiFetch<{ tags: string[] }>(`/api/chats/${chatId}/tags${query({ name })}`, { method: "DELETE" });
/** Кто видит групповой чат: `all` — все с «Чатами», `members` — участники; править — право `chat_groups`. */
export type GroupVisibility = "all" | "members";
export type GroupMember = { user_id: number; name: string; source: "author" | "telegram" | "manual"; hidden: boolean };
export type GroupAccess = {
  visibility: GroupVisibility;
  custom: boolean;
  default: GroupVisibility;
  members: GroupMember[];
  can_edit: boolean;
  people: { user_id: number; name: string }[];
};
export const loadGroupAccess = (chatId: number) => apiFetch<GroupAccess>(`/api/chats/${chatId}/group`);
export const setGroupVisibility = (chatId: number, visibility: GroupVisibility | null) =>
  apiFetch<GroupAccess>(`/api/chats/${chatId}/group/visibility`, {
    method: "POST",
    body: JSON.stringify({ visibility }),
  });
export const addGroupMember = (chatId: number, user_id: number, name: string) =>
  apiFetch<GroupAccess>(`/api/chats/${chatId}/group/members`, {
    method: "POST",
    body: JSON.stringify({ user_id, name }),
  });
export const removeGroupMember = (chatId: number, userId: number) =>
  apiFetch<GroupAccess>(`/api/chats/${chatId}/group/members/${userId}`, { method: "DELETE" });
export const markAnswered = (chatId: number) => apiFetch<Chat>(`/api/chats/${chatId}/answered`, { method: "POST" });
/** Группа «без уведомлений» (`mute`) или снова с ними — только для меня. */
export const muteGroup = (chatId: number, mute: boolean) =>
  apiFetch<{ muted: boolean }>(`/api/chats/${chatId}/mute`, { method: "POST", body: JSON.stringify({ mute }) });
export const loadChat = (chatId: number) => apiFetch<ChatThread>(`/api/chats/${chatId}`);
/** Диалог открыли — мост освежит его галки и историю при ближайшей отметке вкладки (один раз, не опрос). */
export const refreshChat = (chatId: number) =>
  apiFetch<{ queued: boolean }>(`/api/chats/${chatId}/refresh`, { method: "POST" });
/** Вся переписка клиента одной лентой: этот чат и связанные (`related`), по времени. */
export const loadChatHistory = (chatId: number) =>
  apiFetch<{ messages: ChatMessage[]; calls: ChatCall[]; comments?: ChatComment[]; visits?: ChatVisit[]; notices?: ChatNotice[] }>(`/api/chats/${chatId}/history`);
export const attachPhone = (chatId: number, phone: string) =>
  apiFetch<{ phones: ChatPhone[] }>(`/api/chats/${chatId}/phones`, { method: "POST", body: JSON.stringify({ phone }) });
export const detachPhone = (chatId: number, phone: string) =>
  apiFetch<{ phones: ChatPhone[] }>(`/api/chats/${chatId}/phones/${phone}`, { method: "DELETE" });
/**
 * Связь с клиентом для блока «Контакты» (`GET /api/chats/{id}/contacts`). Уровень канала —
 * «можем ли написать из кабинета»: `ok` — да, `partial` — зацепка есть (аккаунт известен,
 * переписка только из браузера сотрудника), `none` — нет или не проверяли. Всё — из своей базы.
 */
export type ContactLevel = "ok" | "partial" | "none";
export type ContactLast = { date: string; out: boolean } | null;
/** Переписка личного аккаунта сотрудника из расширения Chrome — видна, ответить отсюда нельзя. */
export type ContactBrowser = { chat_id: number; account_name: string; last: ContactLast } | null;
/** Аккаунт на номере: `yes`/`no` — знаем (`source`: `check` — проверили своим аккаунтом, `crm` —
 * связка «номер ↔ мессенджер» из своей копии CRM), `unknown` — нет. */
export type ContactAccount = {
  state: "yes" | "no" | "unknown";
  username?: string;
  checked?: string | null;
  source?: "check" | "crm" | null;
};
export type ContactBot = {
  level: ContactLevel;
  /** Чат с ботом: клиент его запустил, пишем из кабинета. `via` — пришёл по ссылке брони или записи. */
  bot: {
    chat_id: number;
    name: string;
    username: string;
    last: ContactLast;
    via: "deal" | "zapis" | null;
    deal_id: number | null;
    since: string | null;
  } | null;
  /** «Авторизован»: клиент вошёл через этот бот в личный кабинет — номер подтвердил сам. */
  auth: { phone: string; since: string | null } | null;
  account: ContactAccount;
  browser: ContactBrowser;
};
export type ClientContacts = {
  /** Личный кабинет `my.uni-renter.ru/login` (`client_auth`): `ok` — номер авторизован, `partial` —
   * вход начат и не закончен (`pending`). `channels` — чем подтверждал: telegram / max / push;
   * `fitting` — номер сверен с карточкой клиента на примерке. */
  cabinet: {
    level: ContactLevel;
    phones: string[];
    since: string | null;
    last: string | null;
    channels: string[];
    fitting: { stylist: string } | null;
    pending: string | null;
  };
  whatsapp: {
    level: ContactLevel;
    ours: {
      chat_id: number;
      last: ContactLast;
      window: { open: boolean; last_in: string | null; until: string | null };
    } | null;
    browser: ContactBrowser;
  };
  telegram: ContactBot;
  max: ContactBot;
  push: {
    level: ContactLevel;
    live: { device: string; since: string | null; delivered: string | null }[];
    refused: { count: number; last: string | null };
  };
  email: { level: ContactLevel; addresses: string[] };
  marketplaces: { id: "avito" | "vk" | "instagram"; chat_id: number; last: ContactLast; context: string }[];
};
export const loadChatContacts = (chatId: number) => apiFetch<ClientContacts>(`/api/chats/${chatId}/contacts`);
/** Пересчитать сделку диалога по своей базе — после живого поиска в Bitrix. */
export const resolveChat = (chatId: number) =>
  apiFetch<{ phone: string | null; deal: ChatDealInfo | null; lead: ChatLeadInfo | null; phone_checked: boolean }>(
    `/api/chats/${chatId}/resolve`,
    { method: "POST" },
  );
/** `replyTo` — ответ на сообщение этого чата (`ChatMessage.id`). */
/** `chat_id` — куда ушёл ответ: у чата, заведённого по номеру, первый ответ может перейти в найденный диалог. */
export const sendToChat = (chatId: number, text: string, replyTo?: number | null) =>
  apiFetch<{ sent: boolean; chat_id?: number }>(`/api/chats/${chatId}/send`, {
    method: "POST",
    body: JSON.stringify({ text, reply_to: replyTo ?? null }),
  });
/**
 * Файл — телом запроса как есть: тип в `Content-Type`, имя и подпись — в адресе.
 * 413 без JSON — отказ шлюза (nginx), а не сервиса: говорим об этом словами.
 */
/** Откуда фото платья в поле ответа: поиск похожих или каталог. Метка едет с файлом на сервер —
 * вкладка «Клиент» по ней знает, какие платья клиенту отправили. */
export type DressOrigin = { origin: "search" | "catalog" | "serial"; model_id: number; search_id?: number };
const dressOrigin = new WeakMap<File, DressOrigin>();
export const markDress = (file: File, meta: DressOrigin) => (dressOrigin.set(file, meta), file);

export const sendFileToChat = (chatId: number, file: File, caption: string, replyTo?: number | null) =>
  // `warning` — файл ушёл, подпись нет (WhatsApp): повтор файла задвоил бы его клиенту.
  apiFetch<{ sent: boolean; warning?: string }>(`/api/chats/${chatId}/send-file${query({ name: file.name || "file", caption, ...dressOrigin.get(file), reply_to: replyTo ?? undefined })}`, {
    method: "POST",
    body: file,
    headers: { "Content-Type": file.type || "application/octet-stream" },
  }).catch((failure: unknown) => {
    if (failure instanceof ApiFailure && failure.status === 413 && failure.code === "BAD_RESPONSE") {
      throw new Error(`«${file.name}» не прошёл: шлюз кабинета пока не пропускает такие большие файлы`);
    }
    throw failure;
  });

/** Поиск платья по фото клиента — `docs/specs/2026-10-02-dress-search-design.md`. */
export type DressSerial = { serial: number; article: number; size: string; city: string; place: string; available: boolean };
export type DressCard = {
  model_id: number;
  photo_id: number;
  photo_url: string;
  /** Косинус сходства с фото клиента, 0…1. */
  score: number;
  name: string;
  type: string;
  articles: number[];
  price: number | null;
  /** Серии в выбранном городе (без города — во всех); не больше 40, всего — `count`. */
  here: DressSerial[];
  count: number;
  elsewhere: { city: string; count: number }[];
};
export type DressSearch = {
  search_id: number;
  location_id: number | null;
  locations: { id: number; name: string }[];
  here: DressCard[];
  elsewhere: DressCard[];
  /** Расширение: по этому фото уже искали — найденное взято из кэша по хэшу фото, наличие свежее. */
  cached?: boolean;
};
/** Объявление Авито, по которому пишет клиент (`GET /api/chats/{id}/item`); фото — `…/item/photo`. */
export type ChatItem = { title: string; url: string; id: number | null; price: string; city: string; image: boolean };
export const chatItem = (chatId: number) => apiFetch<ChatItem>(`/api/chats/${chatId}/item`);
/** `messageId` нет — ищем по главному фото объявления Авито. */
export const dressSearch = (chatId: number, messageId: number | null, locationId: number | null) =>
  apiFetch<DressSearch>(`/api/chats/${chatId}/dress-search`, {
    method: "POST",
    body: JSON.stringify({ message_id: messageId, location_id: locationId }),
  });
/**
 * Черновик клиенту: фото отмеченных (не отметили — 5 лучших по порядку подборки) и текст со ссылкой
 * `my.uni-renter.ru/p/<хэш>` на остальные похожие платья города (`count` — всего на странице).
 * Отправляет сотрудник сам.
 */
export type DressOffer = {
  text: string;
  text_link: string;
  /** Одна ссылка без фото: «Смотрите, какие похожие платья на ваше есть…» — кнопка в просмотре фото. */
  text_similar: string;
  cards: DressCard[];
  url: string;
  count: number;
};
export const dressPick = (chatId: number, searchId: number, models: number[]) =>
  apiFetch<DressOffer>(`/api/chats/${chatId}/dress-search/${searchId}/pick`, {
    method: "POST",
    body: JSON.stringify({ models }),
  });
/**
 * Все фото модели для просмотра в панели расширения: обложка первой, затем «на человеке» (`view:
 * "person"`), затем вешалка. Обычно 4–5, бывает 1 и 30+. Байты — те же `/api/dress/photo/{id}`.
 */
export type DressModelPhoto = { photo_id: number; photo_url: string; view: "person" | "other" | null; cover: boolean };
export const dressModelPhotos = (modelId: number) =>
  apiFetch<{ model_id: number; photos: DressModelPhoto[] }>(`/api/dress/model/${modelId}/photos`);

/** Каталог с фильтром «Резерва» — `docs/specs/2026-10-02-dress-catalog-design.md`. */
export type CatalogValue = { id: string; name: string; full: string; color: string | null; hide: boolean };
export type CatalogGroup = {
  key: string;
  /** Куда уходит выбор: productCategoryID, locationID, modelPropertyID, similarNum, serial. */
  api: string;
  name: string;
  template: "button" | "text";
  multiple: boolean;
  collapsed: boolean;
  values: CatalogValue[];
};
export type CatalogCard = {
  model_id: number;
  photo_id: number;
  photo_url: string;
  name: string;
  price: number | null;
  articles: number[];
};
/** Выбор в фильтре: ключ группы → id значений; у «Серии» — одна строка с номерами. */
export type CatalogChoice = Record<string, string[]>;
export const catalogQuery = (groups: CatalogGroup[], choice: CatalogChoice) => {
  const by: Record<string, string[]> = {};
  for (const g of groups) (by[g.api] ??= []).push(...(choice[g.key] ?? []));
  const q = new URLSearchParams();
  const names = { productCategoryID: "category", locationID: "locations", modelPropertyID: "props", similarNum: "similar" };
  for (const [api, name] of Object.entries(names)) if (by[api]?.length) q.set(name, by[api].join(","));
  if (by.serial?.length) q.set("serial", by.serial.join(" "));
  return q;
};
export const dressFilters = (category: string[]) =>
  apiFetch<{ groups: CatalogGroup[] }>(`/api/dress/filters?category=${category.join(",")}`);
export const dressCatalog = (query: URLSearchParams, offset: number, limit: number) =>
  apiFetch<{ total: number; offset: number; cards: CatalogCard[] }>(
    `/api/dress/catalog?${new URLSearchParams({ ...Object.fromEntries(query), offset: String(offset), limit: String(limit) })}`,
  );
/** Журнал: какие модели из каталога сотрудник взял в ответ. Клиенту ничего не уходит. */
export const dressCatalogPick = (chatId: number, models: number[], query: URLSearchParams) =>
  apiFetch<{ models: number[] }>(`/api/chats/${chatId}/dress-catalog/pick`, {
    method: "POST",
    body: JSON.stringify({ models, filter: Object.fromEntries(query) }),
  });

/** «MAX · «Сева-2» +7 925 933-67-44» — где шла переписка из браузера: мессенджер, имя номера в CRM, номер. */
export function webAccount(web: NonNullable<NonNullable<ChatBrief["context"]>["web"]>, channel: string): string {
  const phone = web.account_phone ? fmtAccountPhone(web.account_phone) : "";
  const name = web.account_name ? `«${web.account_name}»` : phone ? "" : `аккаунт ${web.account_id}`;
  return [web.platform ?? channel, [name, phone].filter(Boolean).join(" ")].filter(Boolean).join(" · ");
}

/**
 * Адрес диалога у площадки для чата из браузера. Telegram — ник или id собеседника, MAX — id
 * диалога (он свой у каждого аккаунта), WhatsApp — номер клиента: id чата в адрес не ставится.
 */
export function webChatUrl(chat: Pick<Chat, "channel" | "username" | "phone" | "context">): string | null {
  const web = chat.context?.web;
  if (!web) return null;
  if (chat.channel === "telegram") return `https://web.telegram.org/k/#${chat.username ? `@${chat.username}` : web.peer_id}`;
  if (chat.channel === "max") return `https://web.max.ru/${web.peer_id}`;
  const phone = String(chat.phone ?? "").replace(/\D/g, "");
  return phone ? `https://web.whatsapp.com/send?phone=${phone}` : "https://web.whatsapp.com/";
}

/** Российский номер — «+7 925 933-67-44», иностранный — как есть с «+». */
export const fmtAccountPhone = (p: string) =>
  p.length === 11 && p.startsWith("7")
    ? `+7 ${p.slice(1, 4)} ${p.slice(4, 7)}-${p.slice(7, 9)}-${p.slice(9)}`
    : `+${p}`;

/**
 * Каналы — вкладками, как папки Telegram: WhatsApp первым (самый частый), подписи короткие
 * (`short`), полное имя — в шапке чата; цвет — метка канала на аватаре.
 */
export const CHANNELS: { id: Channel; label: string; short: string; mark: string; color: string }[] = [
  { id: "whatsapp", label: "WhatsApp", short: "WA", mark: "W", color: "#25d366" },
  { id: "telegram", label: "Telegram", short: "TG", mark: "T", color: "#2aabee" },
  { id: "max", label: "MAX", short: "MAX", mark: "M", color: "#7c4dff" },
  { id: "avito", label: "Авито", short: "Авито", mark: "A", color: "#00a046" },
  { id: "vk", label: "ВКонтакте", short: "ВК", mark: "В", color: "#0077ff" },
  { id: "instagram", label: "Instagram", short: "Инста", mark: "I", color: "#e1306c" },
  { id: "livechat", label: "Онлайн-чат сайта", short: "Сайт", mark: "С", color: "#ba9c7b" },
];
export const channelOf = (id: Channel) => CHANNELS.find((c) => c.id === id)!;

/** Общий аккаунт канала без имени и номера — что это: клиент пишет боту, в группу. */
export const PLAIN: Partial<Record<Channel, string>> = { telegram: "бот", max: "бот", vk: "группа" };
/**
 * Имя нашего аккаунта: номер — как он назван в справочнике CRM, в кавычках («Wazzup»,
 * «Сева-2»), без названия — сам номер; у Instagram — город. `full` — название и номер вместе.
 * Сервер до выкатки поля `account` его не отдаёт — тогда пусто, останется одно имя канала.
 */
export function accountName(a: ChatBrief["account"] | undefined, full = false): string {
  if (!a) return "";
  const name = (a.web || a.phone) && a.name ? `«${a.name}»` : a.name;
  const phone = a.phone ? fmtAccountPhone(a.phone) : "";
  return full ? [name, phone].filter(Boolean).join(" ") : name || phone;
}
/** Где мы общались, полностью — в шапку чата и подсказки: «WhatsApp · «Wazzup» +7 925 605-23-69», «Telegram · бот». */
export function placeFull(c: ChatBrief): string {
  const ch = channelOf(c.channel);
  const name = accountName(c.account, true);
  // Аккаунт сотрудника без имени и номера — «аккаунт <id>» из данных расширения.
  if (c.context?.web) return `${name ? `${ch.label} · ${name}` : webAccount(c.context.web, ch.label)} · из браузера`;
  return [ch.label, name || PLAIN[c.channel]].filter(Boolean).join(" · ");
}

/** Лайки клиента на странице подборки `my.uni-renter.ru/p/<хэш>` — блок «Понравилось клиенту». */
export type LikedSerial = { serial: number; price: number; sale: boolean; city: string };
export type LikedDress = {
  model_id: number;
  photo: string;
  name: string;
  in_stock: boolean;
  liked_at: string;
  serials: LikedSerial[];
};
export type LikedSelection = {
  token: string;
  url: string;
  open_count: number;
  open_last: string | null;
  go_last: string | null;
  go_channel: string | null;
  likes: LikedDress[];
};
export type ChatLikes = { stock_checked: boolean; selections: LikedSelection[] };
export const loadChatLikes = (chatId: number) => apiFetch<ChatLikes>(`/api/chats/${chatId}/likes`);

/** Вкладка «Клиент»: чем интересовался клиент — `GET /api/chats/{id}/client`.
 * Спека `docs/specs/2026-10-03-chat-client-interest-design.md`. */
export type ClientDress = {
  model_id: number;
  name: string;
  serial: number | null;
  price: number | null;
  sale: boolean;
  city: string | null;
  in_stock: boolean;
  photo: string;
  /** Категория каталога CRM для фильтра; у платьев — с типом: «Платья свадебные». */
  category?: string;
};
export type ClientOffer = {
  search_id: number;
  count: number;
  url: string;
  at: string;
  user: string;
  picked: number;
  props: string[];
  open_count: number;
  open_last: string | null;
  go_channel: string | null;
  go_last: string | null;
  /** `link` — ссылка ушла клиенту, `photos` — ушли фото, `none` — отправка не подтверждена. */
  sent: "link" | "photos" | "none";
  sent_photos: number;
};
export type ClientSearch = {
  key: string;
  photo: { url: string | null; text: string | null };
  source: "chat" | "ext" | "site" | "top";
  /** Расширение: `hover` — навели курсор, `viewer` — кнопка просмотра, `panel` — кнопка панели. */
  how: string;
  user: string;
  chat_id: number | null;
  channel: Channel | null;
  web: boolean;
  first: string;
  last: string;
  times: number;
  cities: { id: number; name: string }[];
  found: number;
  top: (ClientDress & { picked: boolean })[];
  offer: ClientOffer | null;
};
export type ClientHeart = {
  serial: number;
  model_id: number | null;
  name: string;
  size: string;
  price: number | null;
  status: string;
  to_deal: boolean;
  photo: string;
  user: string;
  at: string | null;
};
export type LikedSrc = "fit" | "site" | "lk";
export type ClientLiked = {
  key: string;
  model_id: number | null;
  photo: string;
  serial: number | null;
  size: string;
  price: number | null;
  sale: boolean;
  city: string | null;
  in_stock: boolean;
  /** Категория каталога CRM для фильтра («Фата», «Платья свадебные»); пусто — модель не известна. */
  category?: string;
  /** Порядок группы: `sortOrder` категории CRM × 10 (+ тип платья); без модели — в конце. */
  category_sort?: number;
  at: string | null;
  /** Свежие первыми. `kind`: fit — like/deal/client_like; site — order/wish; lk — selection/top. */
  sources: {
    src: LikedSrc;
    kind: string;
    at: string | null;
    status?: string;
    user?: string;
    order_id?: number;
    /** Сердечко сайта: `browser` — тот же браузер, что оформил заказ; `account` — та же учётка сайта. */
    via?: "browser" | "account";
    url?: string;
  }[];
};
export type ClientInterest = {
  crm: boolean;
  client: {
    chats: { id: number; channel: Channel; web: boolean; name: string; current: boolean }[];
    phones: string[];
    /** Город чата — салон по умолчанию у похожих. */
    location_id?: number | null;
    deal: { id: number; stage: string; kind: string } | null;
  };
  summary: {
    types: { type: string; share: number; strong: boolean }[];
    sizes: { value: string; from: "salon" | "filter" | "text" }[];
    colors: { value: string; n: number }[];
    budget: { min: number; max: number } | null;
    cities: { id: number; name: string; n: number }[];
    repeats: (ClientDress & { sources: string[] })[];
    counts: { searches: number; likes: number; salon_liked: number; offered: number; site_liked?: number };
  };
  /** Сетка «понравилось» вкладки «Товар»: примерка, сайт, личный кабинет (спека 2026-10-22-site-orders-interest). */
  liked?: ClientLiked[];
  site?: { checked: boolean; synced_at: string | null; orders: number[] };
  likes: ChatLikes;
  salon: { liked: ClientHeart[]; disliked: ClientHeart[]; in_work: number } | null;
  searches: ClientSearch[];
  catalog: { at: string; user: string; chat_id: number | null; filter: string[]; models: ClientDress[]; sent_photos: number }[];
  unsearched: {
    chat_id: number;
    channel: Channel;
    web: boolean;
    message_id: number;
    at: string;
    kind: string;
    photo: string | null;
    note: string;
    ext: boolean;
  }[];
  bot: { at: string; chat_id: number; channel: Channel; kind: "serial" | "selection"; serial: number | null; token: string }[];
};
export const loadChatClient = (chatId: number) => apiFetch<ClientInterest>(`/api/chats/${chatId}/client`);

/** Наш номер WhatsApp в служебном браузере, с которого можно написать из «Чатов» (`GET /api/chats/start`). */
export type StartAccount = { account: string; phone: string; title: string; port: number };
export type StartedChat = { chat_id: number; account: string; accounts: StartAccount[]; created: boolean };

/**
 * Чат с номером без переписки: пустой чат WhatsApp нашего номера `account` (нет — первого из
 * доступных). В списке не виден, пока пуст; клиенту ничего не пишет. Спека
 * `docs/specs/2026-10-22-chat-by-phone-design.md`.
 */
/** `deal` — сделка, по которой клиента нашли: новому чату закрепляется, панель покажет её и без номера в копии. */
export const startChat = (phone: string, account: string | null, deal: number | null = null, item = "") =>
  apiFetch<StartedChat>("/api/chats/start", { method: "POST", body: JSON.stringify({ phone, account, deal, item }) });
